import React, { useMemo } from "react";
import { Pencil, ExternalLink } from "lucide-react";
import { money2 } from "../payrollHelpers";

export default function PieceRateTab({ row, adjustments, canPayroll, onEditPieceRate, onViewCustomer, isLocked }) {
  const canEdit = (canPayroll("edit_commissions") || canPayroll("add_adjustments")) && !isLocked;
  // Group adjustments by jobId. One job's piece-rate lines are usually a
  // single product, so job-level grouping is precise enough for the "Adjustment" column.
  const adjByJob = useMemo(() => {
    const m = {};
    (adjustments || []).filter((a) => a.category === "PIECE_RATE" && a.jobId).forEach((a) => {
      (m[a.jobId] = m[a.jobId] || []).push(a);
    });
    return m;
  }, [adjustments]);

  const lines = row.pieceRateLines || [];

  return (
    <div data-testid="payroll-piece-rate-tab">
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
              <th className="px-2 py-1.5 text-left">Job #</th>
              <th className="px-2 py-1.5 text-left">Customer</th>
              <th className="px-2 py-1.5 text-left">Service/Product</th>
              <th className="px-2 py-1.5 text-right">Quantity</th>
              <th className="px-2 py-1.5 text-right">Rate</th>
              <th className="px-2 py-1.5 text-right">System Total</th>
              <th className="px-2 py-1.5 text-right">Adjustment</th>
              <th className="px-2 py-1.5 text-right">Final</th>
              <th className="px-2 py-1.5 text-right min-w-[70px] whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.map((l, idx) => {
              const jobAdjustments = adjByJob[l.jobId] || [];
              const adjTotal = jobAdjustments.reduce((s, a) => s + a.adjustmentAmount, 0);
              const finalAmount = l.total + adjTotal;
              return (
                <tr key={`${l.jobId}-${l.pricebookId}-${idx}`} data-testid={`piece-rate-line-${l.jobId}-${idx}`}>
                  <td className="px-2 py-1.5 font-medium text-slate-700">{l.jobId}</td>
                  <td className="px-2 py-1.5">
                    <button type="button" onClick={() => l.customerId && onViewCustomer(l.customerId)} disabled={!l.customerId} className="text-blue-600 hover:underline font-medium disabled:text-slate-500 disabled:no-underline">
                      {l.customer}
                    </button>
                  </td>
                  <td className="px-2 py-1.5">{l.product}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{l.qty}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{money2(l.rate)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-500">{money2(l.total)}</td>
                  <td className={`px-2 py-1.5 text-right ${adjTotal > 0 ? "text-emerald-600" : adjTotal < 0 ? "text-rose-600" : "text-slate-300"}`}>{adjTotal !== 0 ? money2(adjTotal) : "—"}</td>
                  <td className="px-2 py-1.5 text-right font-semibold">{money2(finalAmount)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {canEdit && (
                      <button type="button" onClick={() => onEditPieceRate(l, l.total)} data-testid={`piece-rate-edit-${l.jobId}-${idx}`} title="Edit piece rate" className="h-6 w-6 rounded border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><Pencil className="h-3 w-3 text-slate-500" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
            {lines.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-6 text-center text-[12px] text-slate-400 italic">
                {row.pay.model === "piece_rate" ? "No piece-rate items found on this tech's completed jobs for the selected range." : "This technician isn't on the piece-rate pay model."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-slate-400 italic flex items-center gap-1">
        <ExternalLink className="h-3 w-3" /> Piece-rate lines are auto-calculated from completed jobs' invoice items tagged with a per-unit rate in the Pricebook.
      </div>
    </div>
  );
}
